import React from 'react';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

/**
 * Class-based error boundary so a render-time crash inside any wrapped child
 * never produces a blank white screen. We auto-report the error to /api/log/error
 * (best-effort), and offer the user a recovery path: go back, retry, or go home.
 *
 * Used to wrap pages that fetch external data or contain third-party widgets —
 * notably BusinessDetail, where a missing import was crashing the whole tree
 * for any business with an upcoming event.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) || 'Unexpected error' };
  }

  componentDidCatch(error, info) {
    // Best-effort error reporting — never blocks render.
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', error, info?.componentStack);
      let userId = '';
      try {
        const stored = localStorage.getItem('user');
        if (stored) { userId = (JSON.parse(stored) || {}).id || ''; }
      } catch (_) { /* noop */ }
      const payload = {
        message: String(error?.message || error),
        // /api/errors/report stores `details` from this field — pack stack + componentStack here.
        stack: [
          String(error?.stack || ''),
          info?.componentStack ? `\n--- componentStack ---\n${info.componentStack}` : '',
          typeof navigator !== 'undefined' && navigator.userAgent ? `\n--- ua ---\n${navigator.userAgent}` : '',
        ].filter(Boolean).join(''),
        url: typeof window !== 'undefined' ? window.location.href : '',
        userId,
      };
      fetch(`${API}/errors/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch (_) { /* noop */ }
  }

  reset = () => {
    this.setState({ hasError: false, message: '' });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="error-boundary-fallback">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-6 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-3">
            <AlertTriangle className="h-6 w-6 text-amber-600" />
          </div>
          <h1 className="text-lg font-bold text-gray-900 mb-1">Something went wrong</h1>
          <p className="text-sm text-gray-600 mb-4">
            We couldn't display this page. We've logged the issue so we can fix it.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              onClick={() => { try { window.history.back(); } catch (_) { window.location.href = '/'; } }}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200"
              data-testid="error-boundary-back"
            >
              <ArrowLeft className="h-4 w-4" /> Go back
            </button>
            <button
              onClick={this.reset}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700"
              data-testid="error-boundary-retry"
            >
              Try again
            </button>
            <button
              onClick={() => { window.location.href = '/'; }}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50"
              data-testid="error-boundary-home"
            >
              Home
            </button>
          </div>
          {this.state.message && (
            <p className="text-[11px] text-gray-400 mt-4 break-words">
              {this.state.message.slice(0, 200)}
            </p>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
